import {
  Area,
  CartesianGrid,
  ComposedChart,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts"
import type { TooltipContentProps } from "recharts"
import { useTranslation } from "react-i18next"
import { useTenantCurrency } from "@/lib/use-tenant-currency"
import { formatTableValue } from "../../lib/format-table-value"
import type { PerformanceKpiId } from "../../lib/my-brand-drawer-performance-catalog"
import { MY_BRAND_DRAWER_FATIGUE_CATALOG } from "../../lib/my-brand-drawer-fatigue-catalog"
import { fatigueKpiColorVar } from "../../lib/my-brand-drawer-fatigue-colors"
import { useKpiCatalog } from "@/features/overview/hooks/useKpiCatalog"
import type { FatigueChartRow, FatigueSeriesPoint } from "../../lib/my-brand-drawer-fatigue-model"

const CHART_AXIS_TICK = { fontSize: 11, fill: "var(--muted-foreground)" } as const

const FATIGUE_LINE_ANIMATION_MS = 900

interface FatigueChartProps {
  rows: FatigueChartRow[]
  points: FatigueSeriesPoint[]
  selectedIds: PerformanceKpiId[]
  labelsById: Record<PerformanceKpiId, string>
  indexed: boolean
  showPrevious: boolean
}

function formatYTick(
  value: number,
  indexed: boolean,
  currencySymbol: string,
  singleFormat?: (typeof MY_BRAND_DRAWER_FATIGUE_CATALOG)[number]["format"],
): string {
  if (indexed) return `${Math.round(value)}`
  if (singleFormat) return formatTableValue(value, singleFormat)
  if (Math.abs(value) >= 1_000_000) return `${(value / 1_000_000).toFixed(1)}M`
  if (Math.abs(value) >= 1_000) return `${(value / 1_000).toFixed(0)}K`
  return `${currencySymbol}${value.toFixed(0)}`
}

function FatigueTooltip({
  active,
  payload,
  label,
  labelsById,
  indexed,
  points,
  currentLabel,
  previousLabel,
  formatIndexedSuffix,
}: TooltipContentProps<number, string> & {
  labelsById: Record<PerformanceKpiId, string>
  indexed: boolean
  points: FatigueSeriesPoint[]
  currentLabel: string
  previousLabel: string
  formatIndexedSuffix: (value: number) => string
}) {
  if (!active || !payload?.length) return null

  return (
    <div className="rounded-lg border bg-card p-3 text-xs shadow-md">
      <p className="mb-2 text-muted-foreground">{label}</p>
      {payload.map((entry) => {
        if (entry.value === undefined || entry.value === null) return null
        const dataKey = String(entry.dataKey)
        const isCurrent = dataKey.endsWith("_cur")
        const metricId = dataKey.replace(/_(?:cur|pre)$/, "") as PerformanceKpiId
        const catalog = MY_BRAND_DRAWER_FATIGUE_CATALOG.find((e) => e.id === metricId)
        if (!catalog) return null
        const bucketLabel = String(label ?? "")
        const period = isCurrent ? "current" : "previous"
        const absolutePoint = points.find(
          (p) =>
            p.metric === catalog.sqlSuffix &&
            p.period === period &&
            p.date === bucketLabel,
        )
        const displayValue =
          absolutePoint?.value !== null && absolutePoint?.value !== undefined
            ? formatTableValue(absolutePoint.value, catalog.format)
            : formatTableValue(entry.value as number, catalog.format)
        const indexedSuffix =
          indexed && entry.value !== null
            ? formatIndexedSuffix(Number(entry.value))
            : ""
        return (
          <div key={dataKey} className="flex items-center gap-2">
            <span
              className="h-2 w-2 rounded-full shrink-0"
              style={{ background: entry.stroke }}
            />
            <span className="text-muted-foreground">{labelsById[metricId]}</span>
            <span className="font-medium text-foreground">
              {displayValue}
              {indexedSuffix}
            </span>
            <span className="text-muted-foreground">
              ({isCurrent ? currentLabel : previousLabel})
            </span>
          </div>
        )
      })}
    </div>
  )
}

export function DrilldownMyBrandCreativeFatigueChart({
  rows,
  points,
  selectedIds,
  labelsById,
  indexed,
  showPrevious,
}: FatigueChartProps) {
  const { t } = useTranslation()
  const { myBrandById } = useKpiCatalog("drilldown")
  const { symbol: currencySymbol } = useTenantCurrency()
  const singleKpiFormat =
    !indexed && selectedIds.length === 1
      ? MY_BRAND_DRAWER_FATIGUE_CATALOG.find((e) => e.id === selectedIds[0])?.format
      : undefined
  const formatIndexedSuffix = (value: number) =>
    t("drilldown.myBrandDrawer.fatigue.indexedSuffix", " · {{value}} index", {
      value: value.toFixed(1),
    })

  if (selectedIds.length === 0) {
    return (
      <div className="flex min-h-[340px] items-center justify-center rounded-[14px] border bg-card p-6">
        <p className="text-sm text-muted-foreground">
          {t("drilldown.myBrandDrawer.fatigue.selectKpi", "Select at least one KPI to chart.")}
        </p>
      </div>
    )
  }

  return (
    <div className="min-h-[340px] w-full rounded-[14px] border bg-card p-4">
      <ResponsiveContainer width="100%" height={340}>
        <ComposedChart data={rows} margin={{ top: 8, right: 16, bottom: 8, left: 8 }}>
          <defs>
            {selectedIds.map((id) => (
              <linearGradient
                key={`fatigue-gradient-${id}`}
                id={`fatigue-gradient-${id}`}
                x1="0"
                y1="0"
                x2="0"
                y2="1"
              >
                <stop offset="0%" stopColor={fatigueKpiColorVar(id, myBrandById)} stopOpacity={0.3} />
                <stop offset="100%" stopColor={fatigueKpiColorVar(id, myBrandById)} stopOpacity={0} />
              </linearGradient>
            ))}
          </defs>
          <CartesianGrid stroke="var(--border)" strokeDasharray="3 3" vertical={false} />
          <XAxis
            dataKey="bucketLabel"
            tick={CHART_AXIS_TICK}
            tickLine={false}
            axisLine={false}
          />
          <YAxis
            tickFormatter={(v) => formatYTick(v, indexed, currencySymbol, singleKpiFormat)}
            tick={CHART_AXIS_TICK}
            tickLine={false}
            axisLine={false}
            width={48}
            label={
              indexed
                ? {
                    value: t("drilldown.myBrandDrawer.fatigue.indexedAxis", "Index (100 = start)"),
                    angle: -90,
                    position: "insideLeft",
                    style: { fontSize: 10, fill: "var(--muted-foreground)" },
                  }
                : undefined
            }
          />
          <Tooltip
            content={(props) => (
              <FatigueTooltip
                {...(props as TooltipContentProps<number, string>)}
                labelsById={labelsById}
                indexed={indexed}
                points={points}
                currentLabel={t("drilldown.myBrandDrawer.fatigue.legendCurrent", "Current")}
                previousLabel={t("drilldown.myBrandDrawer.fatigue.legendPrevious", "Previous")}
                formatIndexedSuffix={formatIndexedSuffix}
              />
            )}
          />
          {selectedIds.flatMap((id) => {
            const color = fatigueKpiColorVar(id, myBrandById)
            const lines = [
              <Area
                key={`${id}_cur_area`}
                type="monotone"
                dataKey={`${id}_cur`}
                stroke="none"
                fill={`url(#fatigue-gradient-${id})`}
                dot={false}
                activeDot={false}
                connectNulls
                animationDuration={FATIGUE_LINE_ANIMATION_MS}
                animationEasing="linear"
              />,
              <Line
                key={`${id}_cur`}
                type="monotone"
                dataKey={`${id}_cur`}
                stroke={color}
                strokeWidth={2}
                dot={false}
                connectNulls
                animationDuration={FATIGUE_LINE_ANIMATION_MS}
                animationEasing="linear"
              />,
            ]
            if (showPrevious) {
              lines.push(
                <Line
                  key={`${id}_pre`}
                  type="monotone"
                  dataKey={`${id}_pre`}
                  stroke={color}
                  strokeWidth={1.5}
                  strokeDasharray="5 5"
                  strokeOpacity={0.55}
                  dot={false}
                  connectNulls
                />,
              )
            }
            return lines
          })}
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  )
}
